import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import api from "@/lib/api";
import { stashReferral } from "@/lib/referralStash";

const JoinPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [referrerName, setReferrerName] = useState(null);

  useEffect(() => {
    const lookupReferral = async () => {
      try {
        // Lookup the referral slug to get the referral code
        const response = await api.get(`/referral/lookup/${slug}`);
        
        if (response.data.valid && response.data.referral_code) {
          const code = response.data.referral_code;
          // Phase 15.5 P5: stash referral so it survives OAuth round-trip
          stashReferral(code);
          // Phase 15.5 P5: log click for the future Auto-Recovery scan
          try {
            await api.post('/referral/track-click', { referral_code: code });
          } catch { /* best-effort */ }
          // Store referrer name for meta tags
          if (response.data.referrer_name) {
            setReferrerName(response.data.referrer_name);
          }
          // Redirect to login page with referral code
          navigate(`/login?ref=${code}`, { replace: true });
        } else {
          setError("Invalid referral link");
          // Redirect to regular login after 2 seconds
          setTimeout(() => navigate('/login', { replace: true }), 2000);
        }
      } catch (err) {
        console.error('Referral lookup failed:', err);
        setError("Invalid or expired referral link");
        // Redirect to regular login after 2 seconds
        setTimeout(() => navigate('/login', { replace: true }), 2000);
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      lookupReferral();
    } else {
      navigate('/login', { replace: true });
    }
  }, [slug, navigate]);

  const ogTitle = referrerName 
    ? `${referrerName} invited you to RGM Studio` 
    : "You've been invited to RGM Studio";
  const ogDescription = "Join RGM Studio to upload music, get featured, and grow your fanbase worldwide.";

  return (
    <>
      <Helmet>
        <title>{ogTitle}</title>
        <meta name="description" content={ogDescription} />
        <meta property="og:title" content={ogTitle} />
        <meta property="og:description" content={ogDescription} />
        <meta property="og:image" content="https://www.rgmstudio.org/rgm-og-image.png" />
        <meta property="og:url" content={`https://www.rgmstudio.org/join/${slug}`} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={ogTitle} />
        <meta name="twitter:description" content={ogDescription} />
        <meta name="twitter:image" content="https://www.rgmstudio.org/rgm-og-image.png" />
      </Helmet>
      <div className="min-h-screen bg-[#08080c] flex items-center justify-center">
        <div className="text-center">
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500 mx-auto mb-4"></div>
              <p className="text-white text-lg">Processing your invite...</p>
            </>
          ) : error ? (
            <>
              <p className="text-red-400 text-lg mb-2">{error}</p>
              <p className="text-gray-400">Redirecting to login...</p>
            </>
          ) : (
            <p className="text-white">Redirecting...</p>
          )}
        </div>
      </div>
    </>
  );
};

export default JoinPage;
